import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, CheckCircle2, Loader2, MessageCircle, CalendarCheck } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Reveal, Eyebrow, SectionTitle } from "./Reveal";
import { API, BOOKING_SLOTS, SERVICES, WA_DEFAULT } from "@/data/content";

const fmt = (d) => d.toLocaleDateString("en-CA");
const inputCls =
  "w-full rounded-lg border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-dim outline-none transition-colors duration-200 focus:border-brand";

export default function BookingSection() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [booked, setBooked] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: SERVICES[0].title,
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!date) return;
    setSlot(null);
    setLoadingSlots(true);
    axios
      .get(`${API}/bookings/slots`, { params: { date: fmt(date) } })
      .then(({ data }) => setBooked(data.booked))
      .catch(() => setBooked([]))
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        date: fmt(date),
        slot,
      });
      setConfirmed(data);
      toast.success("Consultation booked. Our desk will confirm shortly.");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error("That slot was just taken — please pick another time.");
        setSlot(null);
        const { data } = await axios.get(`${API}/bookings/slots`, {
          params: { date: fmt(date) },
        });
        setBooked(data.booked);
      } else {
        toast.error("Booking failed. Please try again or use WhatsApp.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative border-t border-line bg-surface py-24"
    >
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <div className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <Eyebrow>Direct Scheduling</Eyebrow>
              <SectionTitle className="mt-3">
                Book a <span className="text-brand">Consultation</span>
              </SectionTitle>
            </div>
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
              <Clock size={12} className="text-brand" />
              45-min strategy call · All times UTC
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-2xl border border-line bg-panel">
            <div className="grid lg:grid-cols-2">
              <div className="border-b border-line p-6 sm:p-8 lg:border-b-0 lg:border-r">
                <p className="mb-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                  01 — Select a date
                </p>
                <div data-testid="booking-date-calendar" className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                    className="rounded-xl border border-line bg-surface text-ink"
                    classNames={{
                      day_selected:
                        "bg-brand text-white hover:bg-brand focus:bg-brand",
                      day_today: "border border-brand/50 text-brand",
                      head_cell: "text-dim rounded-md w-9 font-mono text-[0.7rem] uppercase",
                      day: "h-9 w-9 p-0 font-mono text-xs text-mute hover:text-ink aria-selected:opacity-100 transition-colors",
                      caption_label: "font-display text-base font-bold uppercase tracking-widest text-ink",
                      nav_button:
                        "h-7 w-7 bg-transparent p-0 text-dim hover:text-brand border border-line rounded-md transition-colors inline-flex items-center justify-center",
                    }}
                  />
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <p className="mb-4 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                  02 — Pick a time {date && `· ${fmt(date)}`}
                </p>
                {!date ? (
                  <div className="flex h-24 items-center justify-center rounded-xl border border-dashed border-line font-mono text-xs text-dim">
                    Select a date to see available slots
                  </div>
                ) : loadingSlots ? (
                  <div className="flex h-24 items-center justify-center">
                    <Loader2 size={20} className="animate-spin text-brand" />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {BOOKING_SLOTS.map((t) => {
                      const taken = booked.includes(t);
                      const active = slot === t;
                      return (
                        <button
                          key={t}
                          data-testid={`booking-slot-${t.replace(":", "")}`}
                          type="button"
                          disabled={taken}
                          onClick={() => setSlot(t)}
                          className={`rounded-lg border py-2.5 font-mono text-xs font-bold transition-colors duration-200 ${
                            taken
                              ? "cursor-not-allowed border-line text-dim line-through opacity-40"
                              : active
                                ? "border-brand bg-brand text-white"
                                : "border-line text-mute hover:border-brand hover:text-brand"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                )}

                {confirmed ? (
                  <div
                    data-testid="booking-confirmation"
                    className="mt-8 rounded-xl border border-up/40 bg-up/5 p-6"
                  >
                    <p className="flex items-center gap-2 font-display text-xl font-bold uppercase tracking-wide text-up">
                      <CheckCircle2 size={18} />
                      Slot Reserved
                    </p>
                    <p className="mt-3 font-mono text-xs leading-relaxed text-mute">
                      {confirmed.service} — {confirmed.date} at {confirmed.slot} UTC.
                      Our desk will confirm your session via email or WhatsApp.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-3">
                      <a
                        data-testid="booking-confirm-whatsapp"
                        href={WA_DEFAULT}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-wa px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white"
                      >
                        <MessageCircle size={13} />
                        Confirm Faster on WhatsApp
                      </a>
                      <button
                        data-testid="booking-another-button"
                        onClick={() => {
                          setConfirmed(null);
                          setSlot(null);
                        }}
                        className="rounded-full border border-line px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-mute transition-colors hover:border-brand hover:text-brand"
                      >
                        Book Another
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submit} className="mt-8 space-y-3">
                    <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">
                      03 — Your details
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        data-testid="booking-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Full name"
                        className={inputCls}
                      />
                      <input
                        data-testid="booking-email-input"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="Email address"
                        className={inputCls}
                      />
                      <input
                        data-testid="booking-phone-input"
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="Phone / WhatsApp (optional)"
                        className={inputCls}
                      />
                      <select
                        data-testid="booking-service-select"
                        value={form.service}
                        onChange={set("service")}
                        className={inputCls}
                      >
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                        <option value="General Inquiry">General Inquiry</option>
                      </select>
                    </div>
                    <textarea
                      data-testid="booking-notes-input"
                      value={form.notes}
                      onChange={set("notes")}
                      placeholder="What would you like to cover? (optional)"
                      rows={3}
                      className={`${inputCls} resize-none`}
                    />
                    <button
                      data-testid="booking-submit-button"
                      type="submit"
                      disabled={!date || !slot || sending}
                      className="inline-flex items-center gap-2.5 rounded-full bg-brand px-8 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-brand-crimson disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {sending ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <CalendarCheck size={15} />
                      )}
                      Reserve Slot
                    </button>
                    <p className="font-mono text-[10px] leading-relaxed tracking-[0.08em] text-dim">
                      Booking requests an educational consultation only — not
                      financial advice or investment products.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
